import { audiencerateApi } from '@/services/audiencerate';
import {
  DataProfileDTO,
  GetMarketIntelligenceReportArgs,
  GetMarketIntelligenceReportResponse,
  GetMarketIntelligenceReportsArgs,
  GetMarketIntelligenceReportsResponse,
  GetMarketIntelligenceDataProfileArgs,
  RefreshInProgressDTO,
  RetryMarketIntelligenceReportArgs,
  RetryMarketIntelligenceReportResponse,
  RefreshMarketIntelligenceDataProfileArgs,
  StartMarketIntelligenceAnalysisArgs,
  StartMarketIntelligenceAnalysisResponse,
} from './types';

export const marketIntelligenceApi = audiencerateApi.injectEndpoints({
  endpoints: (build) => ({
    startMarketIntelligenceAnalysis: build.mutation<StartMarketIntelligenceAnalysisResponse, StartMarketIntelligenceAnalysisArgs>({
      query: ({ workspaceId, body }) => ({
        url: `/v1/workspaces/${workspaceId}/market-intelligence/analyze`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) => [
        { type: 'MarketIntelligenceReports', id: 'LIST' },
        ...(result ? [{ type: 'MarketIntelligenceReports' as const, id: result.reportId }] : []),
      ],
    }),
    getMarketIntelligenceReport: build.query<GetMarketIntelligenceReportResponse, GetMarketIntelligenceReportArgs>({
      query: ({ workspaceId, reportId }) => ({
        url: `/v1/workspaces/${workspaceId}/market-intelligence/${reportId}`,
        method: 'GET',
      }),
      providesTags: (result, error, { reportId }) => [{ type: 'MarketIntelligenceReports', id: result?.reportId || reportId }],
    }),
    retryMarketIntelligenceReport: build.mutation<RetryMarketIntelligenceReportResponse, RetryMarketIntelligenceReportArgs>({
      query: ({ workspaceId, reportId, body }) => ({
        url: `/v1/workspaces/${workspaceId}/market-intelligence/${reportId}/retry`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { reportId }) => [
        { type: 'MarketIntelligenceReports', id: 'LIST' },
        { type: 'MarketIntelligenceReports', id: result?.reportId || reportId },
      ],
    }),
    getMarketIntelligenceReports: build.query<GetMarketIntelligenceReportsResponse, GetMarketIntelligenceReportsArgs>({
      query: ({ workspaceId, limit = 10, offset = 0, status }) => ({
        url: `/v1/workspaces/${workspaceId}/market-intelligence`,
        method: 'GET',
        params: { limit, offset, ...(status ? { status } : {}) },
      }),
      providesTags: (result) => [
        { type: 'MarketIntelligenceReports', id: 'LIST' },
        ...(result?.items.map((report) => ({ type: 'MarketIntelligenceReports' as const, id: report.reportId })) || []),
      ],
    }),
    getMarketIntelligenceDataProfile: build.query<DataProfileDTO, GetMarketIntelligenceDataProfileArgs>({
      query: ({ workspaceId }) => ({
        url: `/v1/workspaces/${workspaceId}/data-profile`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { workspaceId }) => [{ type: 'MarketIntelligenceDataProfile', id: workspaceId }],
    }),
    refreshMarketIntelligenceDataProfile: build.mutation<DataProfileDTO | RefreshInProgressDTO, RefreshMarketIntelligenceDataProfileArgs>({
      query: ({ workspaceId }) => ({
        url: `/v1/workspaces/${workspaceId}/data-profile/refresh`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { workspaceId }) => [{ type: 'MarketIntelligenceDataProfile', id: workspaceId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMarketIntelligenceDataProfileQuery,
  useGetMarketIntelligenceReportQuery,
  useGetMarketIntelligenceReportsQuery,
  useLazyGetMarketIntelligenceReportQuery,
  useRefreshMarketIntelligenceDataProfileMutation,
  useRetryMarketIntelligenceReportMutation,
  useStartMarketIntelligenceAnalysisMutation,
} = marketIntelligenceApi;
